import { TFunction } from "i18next";
import { ColumnGroup, Grouping } from "library/greco-components";

/**
 * BFM-specific "Group by" configuration for the column-options dialog.
 *
 * The vendored greco-components library is domain-agnostic, so the grouping
 * logic (which is specific to BFM's insurance lines / field naming) lives here
 * and is passed in via the `groupings` prop. Groups are derived from the column
 * `key` patterns since there is no explicit group metadata on the columns.
 */

type Col = { key: string; filter?: any };

// ---------------------------------------------------------------------------
// Insurance line (derived from key prefix)
// ---------------------------------------------------------------------------

// Tested longest/most-specific first so e.g. `hullOnLand` is not swallowed by `hull`.
const LINE_TEST_ORDER = [
  "hullOnLand",
  "machineryBreakdown",
  "fleetLegal",
  "engineTax",
  "mtpl",
  "hull",
  "accident",
  "assistance",
  "legal",
];

const LINE_MAP: {
  [k: string]: { prefixes: string[]; labelKey: string; def: string; order: number };
} = {
  mtpl: { prefixes: ["mtpl"], labelKey: "bfm.columnGroup.mtpl", def: "MTPL", order: 1 },
  hull: { prefixes: ["hull"], labelKey: "bfm.columnGroup.hull", def: "Hull", order: 2 },
  fleetLegal: {
    prefixes: ["fleetlegal"],
    labelKey: "bfm.columnGroup.fleetLegal",
    def: "Fleet legal",
    order: 3,
  },
  hullOnLand: {
    prefixes: ["hullonland"],
    labelKey: "bfm.columnGroup.hullOnLand",
    def: "Hull on land",
    order: 4,
  },
  machineryBreakdown: {
    prefixes: ["machinerybreakdown"],
    labelKey: "bfm.columnGroup.machineryBreakdown",
    def: "Machinery breakdown",
    order: 5,
  },
  accident: {
    prefixes: ["accident"],
    labelKey: "bfm.columnGroup.accident",
    def: "Accident",
    order: 6,
  },
  assistance: {
    prefixes: ["assistance"],
    labelKey: "bfm.columnGroup.assistance",
    def: "Assistance",
    order: 7,
  },
  legal: { prefixes: ["legal"], labelKey: "bfm.columnGroup.legal", def: "Legal", order: 8 },
  engineTax: {
    prefixes: ["horsepowerbasedtax", "enginetax"],
    labelKey: "bfm.columnGroup.engineTax",
    def: "Engine tax",
    order: 9,
  },
};

const detectInsuranceLine = (key: string, t: TFunction): ColumnGroup => {
  const k = key.toLowerCase();
  if (k.startsWith("total")) {
    return { key: "totals", label: t("bfm.columnGroup.totals", "Totals"), order: 10 };
  }
  // bookingAmount<Line> keys don't carry the line prefix at the start.
  const base = k.startsWith("bookingamount") ? k.slice("bookingamount".length) : k;
  for (const lineKey of LINE_TEST_ORDER) {
    const def = LINE_MAP[lineKey];
    if (def.prefixes.some((p) => base.startsWith(p))) {
      return { key: lineKey, label: t(def.labelKey, def.def), order: def.order };
    }
  }
  return { key: "general", label: t("bfm.columnGroup.general", "General"), order: 99 };
};

// ---------------------------------------------------------------------------
// Field type (derived from key pattern + filter type)
// ---------------------------------------------------------------------------

const FIELD_TYPE_LABELS: { [k: string]: { def: string; order: number } } = {
  policyStatus: { def: "Policy status", order: 1 },
  premiums: { def: "Premiums", order: 2 },
  bookingAmounts: { def: "Booking amounts", order: 3 },
  payment: { def: "Payment methods", order: 4 },
  insurer: { def: "Insurer", order: 5 },
  policyDetails: { def: "Policy details", order: 6 },
  dates: { def: "Dates", order: 7 },
  flags: { def: "Flags", order: 8 },
  comments: { def: "Comments", order: 9 },
  other: { def: "Other", order: 99 },
};

const fieldTypeKey = (column: Col): string => {
  const k = column.key.toLowerCase();
  if (/prorata|annualgross|annualnet|premium/.test(k)) return "premiums";
  if (/bookingamount/.test(k)) return "bookingAmounts";
  if (/payment(method|mean)/.test(k)) return "payment";
  if (/insurer/.test(k)) return "insurer";
  if (/policystatus/.test(k)) return "policyStatus";
  if (/policynumber|ispolicyissued|policydocumentdate|isclosingdate/.test(k))
    return "policyDetails";
  if (column.filter?.type === "date" || /date$|datetime|expirydate|startdate|enddate/.test(k))
    return "dates";
  if (column.filter?.type === "boolean" || /^is[a-z]|^has[a-z]/.test(k)) return "flags";
  if (/comment$/.test(k)) return "comments";
  return "other";
};

const detectFieldType = (column: Col, t: TFunction): ColumnGroup => {
  const ftKey = fieldTypeKey(column);
  const { def, order } = FIELD_TYPE_LABELS[ftKey];
  return { key: ftKey, label: t(`bfm.columnGroup.ft.${ftKey}`, def), order };
};

// ---------------------------------------------------------------------------
// Category (curated buckets that fold field types)
// ---------------------------------------------------------------------------

const FT_TO_CATEGORY: { [k: string]: string } = {
  premiums: "financials",
  bookingAmounts: "financials",
  payment: "financials",
  policyStatus: "policy",
  policyDetails: "policy",
  insurer: "insurer",
  dates: "dates",
  flags: "flags",
  comments: "comments",
  other: "vehicle",
};

const CATEGORY_LABELS: { [k: string]: { def: string; order: number } } = {
  vehicle: { def: "Vehicle & general", order: 1 },
  policy: { def: "Policy & status", order: 2 },
  financials: { def: "Financials", order: 3 },
  insurer: { def: "Insurer", order: 4 },
  dates: { def: "Dates", order: 5 },
  flags: { def: "Flags", order: 6 },
  comments: { def: "Comments", order: 7 },
};

const detectCategory = (column: Col, t: TFunction): ColumnGroup => {
  const catKey = FT_TO_CATEGORY[fieldTypeKey(column)] || "vehicle";
  const { def, order } = CATEGORY_LABELS[catKey];
  return { key: catKey, label: t(`bfm.columnGroup.cat.${catKey}`, def), order };
};

// ---------------------------------------------------------------------------

export const getColumnGroupings = (t: TFunction): Grouping[] => [
  {
    key: "insuranceLine",
    label: t("bfm.columnGroup.by.insuranceLine", "Insurance line"),
    groupOf: (c) => detectInsuranceLine(c.key, t),
  },
  {
    key: "fieldType",
    label: t("bfm.columnGroup.by.fieldType", "Field type"),
    groupOf: (c) => detectFieldType(c, t),
  },
  {
    key: "category",
    label: t("bfm.columnGroup.by.category", "Category"),
    groupOf: (c) => detectCategory(c, t),
  },
  {
    key: "alphabetical",
    label: t("bfm.columnGroup.by.alphabetical", "Alphabetical"),
    groupOf: () => null,
  },
];
